import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { isScreenMedium, isScreenBig } from '../../utility'
import './scrollContainer.css'

/** Find highest value we should set currentCard to,
 * ie the first card out of the last group  */
export function findHighestCurrentCard(cards, scrollRate) {
  const totalCards = cards.length
  const visibleCards = scrollRate
  const remainder = totalCards % visibleCards

  let highestIndex = totalCards - remainder
  if (remainder === 0) {
    highestIndex = totalCards - visibleCards
  }

  return highestIndex
}

function ScrollContainer({
  intro = null,
  cards = [],
  backgroundColor = 'white',
}) {
  const [currentCard, setCurrentCard] = useState(0)
  const [indicators, setIndicators] = useState(setupIndicators())
  const [activeIndicator, setActiveIndicator] = useState(0)
  const [scrollPos, setScrollPos] = useState(0)

  const scrollRef = useRef(null)
  const controlsRef = useRef(null)

  function scrollRate() {
    let rate = isScreenBig() ? 4 : isScreenMedium() ? 3 : 1
    if (intro) {
      rate = isScreenBig() ? 3 : isScreenMedium() ? 2 : 1
    }
    return rate
  }

  /** Calculate how many indicators we need and what card each scrolls to on click */
  function setupIndicators() {
    const totalCards = cards.length
    const numIndicators = Math.ceil(totalCards / scrollRate())

    const indicators = []
    for (let i = 0; i < numIndicators; i++) {
      const onLastIndicator = i === numIndicators - 1
      const cardToScrollTo = onLastIndicator
        ? findHighestCurrentCard(cards, scrollRate())
        : scrollRate() * i

      indicators.push({ group: i, scrollTo: cardToScrollTo })
    }
    return indicators
  }

  /* Actually move the carousel */
  function scrollCarousel(cardNum) {
    const slideWidth = scrollRef.current?.children[cardNum]?.offsetWidth
    if (isScreenBig()) {
      scrollRef.current?.setAttribute(
        'style',
        `left: -${slideWidth * cardNum - 10}px`
      )
    } else {
      scrollRef.current?.removeAttribute('style')
      scrollRef.current.scrollTo({
        left: slideWidth * cardNum - 20,
        behavior: 'smooth',
      })
    }
  }

  function scrollLeft() {
    if (currentCard === 0) {
      scrollCarousel(0)
    } else if (currentCard - scrollRate() < 0) {
      scrollCarousel(0)
      setCurrentCard(0)
    } else {
      scrollCarousel(currentCard - scrollRate())
      setCurrentCard((currentCard) => currentCard - scrollRate())
    }
  }

  function scrollRight() {
    const limit = findHighestCurrentCard(cards, scrollRate())
    if (currentCard + scrollRate() > limit) {
      scrollCarousel(limit)
      setCurrentCard(limit)
    } else {
      scrollCarousel(currentCard + scrollRate())
      setCurrentCard((currentCard) => currentCard + scrollRate())
    }
  }

  function scrollToCard(cardNum) {
    setCurrentCard(cardNum)
    scrollCarousel(cardNum)
  }

  useEffect(() => {
    function updateCardAndIndicator() {
      const scroller = scrollRef.current
      const currentScrollPos = scroller?.scrollLeft
      const cardWidth = scroller?.children[0]?.offsetWidth
      const scrolledToEnd =
        currentScrollPos >= scroller?.scrollWidth - scroller?.clientWidth - 5

      const activeInd = indicators.find((indicator, i) => {
        const maxScroll = scroller?.scrollWidth - scroller?.clientWidth
        const isLastCard = i === indicators.length - 1
        const activeMin = indicator.scrollTo * cardWidth - 25
        const activeMax =
          indicators[i + 1]?.scrollTo * cardWidth - 25 || maxScroll

        let active =
          (currentScrollPos >= activeMin && currentScrollPos < activeMax) ||
          (isLastCard && scrolledToEnd)
        if (!isLastCard && scrolledToEnd) {
          active = false
        }
        return active
      })

      setCurrentCard(activeInd?.scrollTo)
      setActiveIndicator(activeInd?.group)
      setScrollPos(currentScrollPos)
    }

    scrollRef.current?.addEventListener('touchend', () => {
      updateCardAndIndicator()
    })
    scrollRef.current?.addEventListener('scroll', () => {
      updateCardAndIndicator()
    })
    return () => {
      scrollRef.current?.removeEventListener('touchend', () => {
        updateCardAndIndicator()
      })
      scrollRef.current?.removeEventListener('scroll', () => {
        updateCardAndIndicator()
      })
    }
  }, [indicators])

  // reset carousel on resize
  useEffect(() => {
    function resetCarousel() {
      setIndicators(setupIndicators())
      setActiveIndicator(0)
      setCurrentCard(0)
      scrollCarousel(0)
    }

    window.addEventListener('resize', () => {
      resetCarousel()
    })
    return () => {
      window.removeEventListener('resize', () => {
        resetCarousel()
      })
    }
  }, [])

  const scrolledToEnd =
    scrollPos >=
    scrollRef.current?.scrollWidth - scrollRef.current?.clientWidth - 5
  const onLastCard = currentCard === findHighestCurrentCard(cards, scrollRate())

  return (
    <div className="u-paddingVert5gu">
      <div className="Grid Grid--withGutter FundCards ScrollCards u-marginBottom4gu">
        {intro && (
          <>
            <div className="u-size1of4 u-sm-sizeFull u-marginBottom4gu intro-section">
              {intro}
            </div>
            <div
              className="u-size3of4
               u-sm-sizeFull u-posRelative scroll-container with-intro"
            >
              <div
                className="scroller u-posRelative u-sizeFull Grid Grid--withGutter u-flexNoWrap"
                ref={scrollRef}
              >
                {cards}
              </div>
              <div
                className={`fader fader--start fader--${
                  backgroundColor || 'white'
                } u-hidden u-md-block u-lg-block`}
              ></div>
              <div
                className={`fader fader--end fader--${
                  backgroundColor || 'white'
                } u-hidden u-lg-block`}
              ></div>
            </div>
          </>
        )}
        {!intro && (
          <div className="u-size4of4 u-posRelative scroll-container no-intro">
            <div
              ref={scrollRef}
              className="scroller u-sizeFull u-posRelative Grid Grid--withGutter u-flexNoWrap"
            >
              {cards}
            </div>
            <div
              className={`fader fader--start fader--${
                backgroundColor || 'white'
              } u-hidden u-lg-block`}
            ></div>
            <div
              className={`fader fader--end fader--${
                backgroundColor || 'white'
              } u-hidden u-lg-block`}
            ></div>
          </div>
        )}
      </div>

      <div
        ref={controlsRef}
        className="controls u-sizeConstrained u-posRelative"
      >
        <button
          className="Carousel-btn Carousel-btn--left u-posAbsolute u-textColorBrandLight"
          disabled={
            (!isScreenBig() && scrollPos === 0) ||
            (isScreenBig() && currentCard === 0)
          }
          data-control="prev"
          onClick={scrollLeft}
        >
          <i className="Icon--material u-textSizeMsrp3">chevron_left</i>
        </button>
        <div className="indicators u-posAbsolute u-textCenter">
          {indicators.map((indicator, i) => {
            let active = activeIndicator === i
            if (isScreenBig()) {
              active = indicator.scrollTo === currentCard
            }

            return (
              <button
                key={indicator.group}
                data-scrollto={indicator.scrollTo}
                onClick={() => scrollToCard(indicator.scrollTo)}
                className={active ? 'active-indicator' : ''}
              >
                <div
                  className={`indicator-bar ${
                    active ? 'u-bgColorBrandLight' : ''
                  }`}
                ></div>
              </button>
            )
          })}
        </div>
        <button
          className="Carousel-btn Carousel-btn--right u-posAbsolute u-textColorBrandLight"
          disabled={scrolledToEnd || (isScreenBig() && onLastCard)}
          data-control="next"
          onClick={scrollRight}
        >
          <i className="Icon--material u-textSizeMsrp3">chevron_right</i>
        </button>
      </div>
    </div>
  )
}
ScrollContainer.propTypes = {
  intro: PropTypes.node,
  cards: PropTypes.arrayOf(PropTypes.node),
  backgroundColor: PropTypes.string,
}
export default ScrollContainer
